/* eslint-disable react-hooks/exhaustive-deps */
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faSearch, faBed, faBath, faRulerCombined, faCalendar, faFileContract, faWrench, faCommentDots, faCreditCard, faBell, faCheckCircle, faMapMarkerAlt, faStar, faFileAlt } from "@fortawesome/free-solid-svg-icons";
import { faBuilding, faStar as faStarRegular } from "@fortawesome/free-regular-svg-icons";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getPropertyImageUrl } from "../../../utils/property-image";
import { useLanguage } from "../../../i18n/LanguageContext";
import KycPromptModal from "../kyc-verification/KycPromptModal";
import EmailVerificationBanner from "../../common/EmailVerificationBanner";

function RenterDashboard(){

    const { t } = useLanguage();

    const [renterName, setRenterName] = useState("");
    const [hasActiveRental, setHasActiveRental] = useState(false);
    const [allRentals, setAllRentals] = useState([]);
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const [rentalData, setRentalData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showTerminationModal, setShowTerminationModal] = useState(false);
    const [terminationReason, setTerminationReason] = useState("");
    const [submittingTermination, setSubmittingTermination] = useState(false);
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [reviewRating, setReviewRating] = useState(5);
    const [reviewText, setReviewText] = useState("");
    const [submittingReview, setSubmittingReview] = useState(false);
    const [canReview, setCanReview] = useState(false);
    const [currentUserId, setCurrentUserId] = useState(null);
    const [myApplications, setMyApplications] = useState([]);
    const [kycStatus, setKycStatus] = useState(null);
    const [showNotifications, setShowNotifications] = useState(false);
    const [readNotificationIds, setReadNotificationIds] = useState([]);
    const notificationRef = useRef(null);
    const navigate = useNavigate();

    // Helper function to decode JWT and get user ID
    const getUserIdFromToken = () => {
        const token = localStorage.getItem("token");
        if (!token) return null;
        
        try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join(''));
            
            const payload = JSON.parse(jsonPayload);
            return payload.id;
        } catch (error) {
            console.error("Error decoding token:", error);
            return null;
        }
    };

    useEffect(()=>{
        const firstname = localStorage.getItem("firstname");
        const lastname = localStorage.getItem("lastname");
        if(firstname || lastname){
            setRenterName(`${firstname || ""} ${lastname || ""}`.trim());
        }
        else{
            setRenterName("Renter");
        }
        
        // Get user ID from token
        const userId = getUserIdFromToken();
        setCurrentUserId(userId);
        
        fetchRentalData();
        fetchMyApplications();

        // Load KYC status
        const token = localStorage.getItem("token");
        if (token) {
            fetch("/api/kyc/status", { headers: { Authorization: `Bearer ${token}` } })
                .then(r => r.ok ? r.json() : null)
                .then(data => { if (data) setKycStatus(data.kycStatus || "not_submitted"); })
                .catch(() => {});
        }
    },[]);

    useEffect(() => {
        const intervalId = setInterval(() => {
            fetchMyApplications();
        }, 10000);

        return () => clearInterval(intervalId);
    }, []);

    // Intentionally trigger on rentalData changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (rentalData?.rentalId) {
            checkCanReview();
        }
    }, [rentalData]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!showNotifications) return;

        const handleOutsideClick = (event) => {
            if (notificationRef.current && !notificationRef.current.contains(event.target)) {
                setShowNotifications(false);
            }
        };

        document.addEventListener("mousedown", handleOutsideClick);
        return () => document.removeEventListener("mousedown", handleOutsideClick);
    }, [showNotifications]);

    const fetchRentalData = async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            setLoading(false);
            return;
        }

        try {
            const response = await fetch("/api/renter/rentals", {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                
                // Filter only active rentals (not terminated)
                const activeRentals = data.filter(r => r.status !== 'terminated');
                
                if (activeRentals.length > 0) {
                    const processedRentals = activeRentals.map(activeRental => {
                        // Calculate days until payment due (assuming 1st of each month)
                        const today = new Date();
                        const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
                        const daysUntilDue = Math.ceil((nextMonth - today) / (1000 * 60 * 60 * 24));
                        
                        // Calculate months remaining
                        const endDate = new Date(activeRental.endDate);
                        const monthsRemaining = Math.max(0, Math.round((endDate - today) / (1000 * 60 * 60 * 24 * 30)));

                        return {
                            rentalId: activeRental.rentalId || activeRental.id,
                            propertyName: activeRental.propertyName,
                            address: activeRental.address,
                            city: `${activeRental.city}, ${activeRental.state}`,
                            image: activeRental.thumbnail 
                                ? getPropertyImageUrl(activeRental.thumbnail, { width: 420 })
                                : activeRental.photos?.[0]
                                ? getPropertyImageUrl(activeRental.photos[0], { width: 420 })
                                : "https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800",
                            bedrooms: activeRental.bedrooms,
                            bathrooms: activeRental.bathrooms,
                            sqft: activeRental.sqft,
                            monthlyRent: activeRental.monthlyRent,
                            nextPaymentDue: nextMonth.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
                            daysUntilDue: daysUntilDue,
                            leaseEnds: new Date(activeRental.endDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
                            monthsRemaining: monthsRemaining,
                            landlord: activeRental.landlord || "Property Owner",
                            ownerId: activeRental.ownerId,
                            landlordStatus: "Responsive",
                            terminationRequested: activeRental.terminationRequested,
                            terminationRequestedBy: activeRental.terminationRequestedBy,
                            terminationReason: activeRental.terminationReason,
                            terminationApproved: activeRental.terminationApproved,
                            recentActivity: [
                                {
                                    icon: faCheckCircle,
                                    title: "Rental Started",
                                    description: `Lease began on ${new Date(activeRental.startDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`,
                                    date: new Date(activeRental.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
                                    color: "success"
                                }
                            ]
                        };
                    });

                    setAllRentals(processedRentals);
                    setRentalData(processedRentals[0]);
                    setHasActiveRental(true);
                } else {
                    setHasActiveRental(false);
                }
            }
        } catch (error) {
            console.error("Error fetching rental data:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchMyApplications = async () => {
        const token = localStorage.getItem("token");
        if (!token) return;
        try {
            const res = await fetch("/api/renter/applications", { headers: { Authorization: `Bearer ${token}` } });
            if (res.ok) setMyApplications(await res.json());
        } catch (err) { console.error("Error fetching applications:", err); }
    };

    const handleBrowseProperties = () => {
        navigate("/find-property");
    };

    const getApplicationMeta = (status) => {
        if (status === "approved") {
              return { badge: "bg-success", label: t('renterDashboard.statusApproved'), note: t('renterDashboard.noteApproved') };
        }
        if (status === "rejected") {
              return { badge: "bg-danger", label: t('renterDashboard.statusRejected'), note: t('renterDashboard.noteRejected') };
        }
           return { badge: "bg-warning text-dark", label: t('renterDashboard.statusUnderReview'), note: t('renterDashboard.noteUnderReview') };
    };

    const renderApplicationsSection = () => {
        if (myApplications.length === 0) {
            return (
                <div className="card border-0 shadow-sm mt-4" style={{ borderRadius: "12px" }}>
                    <div className="card-body p-4 text-center">
                        <h5 className="fw-bold mb-2">
                            <FontAwesomeIcon icon={faFileAlt} className="me-2 text-primary" />
                                {t('renterDashboard.myApplications')}
                        </h5>
                            <p className="text-muted mb-0">{t('renterDashboard.noApplications')}</p>
                    </div>
                </div>
            );
        }

        return (
            <div className="card border-0 shadow-sm mt-4" style={{ borderRadius: "12px" }}>
                <div className="card-body p-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="fw-bold mb-0">
                            <FontAwesomeIcon icon={faFileAlt} className="me-2 text-primary" />
                                {t('renterDashboard.myApplications')}
                        </h5>
                            <small className="text-muted">{t('renterDashboard.latestUpdates')}</small>
                    </div>

                    <div className="row g-3">
                        {myApplications.map((app) => {
                            const meta = getApplicationMeta(app.status);
                            return (
                                <div key={app.id} className="col-md-6">
                                    <div className="card border-0 bg-light h-100">
                                        <div className="card-body">
                                            <h6 className="fw-bold mb-1">{app.propertyName}</h6>
                                            <p className="text-muted small mb-1">{app.propertyAddress}</p>
                                            <p className="text-muted small mb-2">{t('renterDashboard.ownerLabel')} {app.ownerName}</p>
                                            <div className="d-flex align-items-center gap-2 mb-2">
                                                <span className={`badge rounded-pill ${meta.badge}`}>{meta.label}</span>
                                                <small className="text-muted">{meta.note}</small>
                                            </div>
                                            {app.status === "rejected" && app.rejectionReason && (
                                                <div className="alert alert-danger py-2 px-3 mb-2 small">
                                                      <strong>{t('renterDashboard.reason')}</strong> {app.rejectionReason}
                                                </div>
                                            )}
                                            <p className="text-muted mb-0" style={{ fontSize: "0.75rem" }}>
                                                Submitted {new Date(app.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    };

    const handleRequestTermination = () => {
        setShowTerminationModal(true);
    };

    const handleSubmitTermination = async () => {
        if (!terminationReason.trim()) {
            alert("Please provide a reason for termination");
            return;
        }

        setSubmittingTermination(true);
        const token = localStorage.getItem("token");

        try {
            const response = await fetch(`/api/rentals/${rentalData.rentalId}/request-termination`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({ reason: terminationReason })
            });

            const data = await response.json();

            if (response.ok) {
                alert("Termination request submitted successfully!");
                setShowTerminationModal(false);
                setTerminationReason("");
                fetchRentalData();
            } else {
                alert(data.message || "Failed to submit termination request");
            }
        } catch (error) {
            console.error("Error requesting termination:", error);
            alert("Error connecting to server");
        } finally {
            setSubmittingTermination(false);
        }
    };

    const handleApproveTermination = async () => {
        if (!window.confirm("Are you sure you want to approve this termination request? This will end the lease immediately.")) {
            return;
        }

        const token = localStorage.getItem("token");

        try {
            const response = await fetch(`/api/rentals/${rentalData.rentalId}/approve-termination`, {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            const data = await response.json();

            if (response.ok) {
                alert("Lease terminated successfully!");
                setHasActiveRental(false);
                setRentalData(null);
                fetchRentalData();
            } else {
                alert(data.message || "Failed to approve termination");
            }
        } catch (error) {
            console.error("Error approving termination:", error);
            alert("Error connecting to server");
        }
    };

    const handleCancelTermination = async () => {
        if (!window.confirm("Are you sure you want to cancel the termination request?")) {
            return;
        }

        const token = localStorage.getItem("token");

        try {
            const response = await fetch(`/api/rentals/${rentalData.rentalId}/cancel-termination`, {
                method: "DELETE",
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            const data = await response.json();

            if (response.ok) {
                alert("Termination request cancelled");
                fetchRentalData();
            } else {
                alert(data.message || "Failed to cancel termination");
            }
        } catch (error) {
            console.error("Error cancelling termination:", error);
            alert("Error connecting to server");
        }
    };

    const checkCanReview = async () => {
        const token = localStorage.getItem("token");
        if (!token || !rentalData?.rentalId) return;

        try {
            const response = await fetch(`/api/reviews/can-review/rental/${rentalData.rentalId}`, {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setCanReview(data.canReview);
            }
        } catch (error) {
            console.error("Error checking review eligibility:", error);
        }
    };

    const handleWriteReview = () => {
        setShowReviewModal(true);
    };

    const handleSubmitReview = async () => {
        if (!reviewText.trim()) {
            alert("Please write a review");
            return;
        }

        setSubmittingReview(true);
        const token = localStorage.getItem("token");

        try {
            const response = await fetch("/api/reviews", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({
                    rentalId: rentalData.rentalId,
                    rating: reviewRating,
                    reviewText: reviewText.trim()
                })
            });

            const data = await response.json();

            if (response.ok) {
                alert("Review submitted successfully!");
                setShowReviewModal(false);
                setReviewText("");
                setReviewRating(5);
                checkCanReview();
            } else {
                alert(data.message || "Failed to submit review");
            }
        } catch (error) {
            console.error("Error submitting review:", error);
            alert("Error connecting to server");
        } finally {
            setSubmittingReview(false);
        }
    };

    const renderStars = (rating, interactive = false) => {
        return Array.from({ length: 5 }).map((_, index) => {
            const starValue = index + 1;
            return (
                <FontAwesomeIcon
                    key={index}
                    icon={starValue <= rating ? faStar : faStarRegular}
                    className={interactive ? "text-warning" : "text-warning"}
                    style={{ cursor: interactive ? 'pointer' : 'default', fontSize: interactive ? '1.5rem' : '1rem' }}
                    onClick={interactive ? () => setReviewRating(starValue) : undefined}
                />
            );
        });
    };

    const notifications = (() => {
        const items = [];

        if (rentalData?.daysUntilDue != null && rentalData.daysUntilDue <= 7) {
            items.push({
                id: `rent-due-${rentalData.rentalId}-${rentalData.daysUntilDue}`,
                title: "Rent due soon",
                message: `${rentalData.propertyName}: payment due in ${rentalData.daysUntilDue} day(s).`,
                time: rentalData.nextPaymentDue,
            });
        }

        if (rentalData?.terminationRequested) {
            if (rentalData.terminationRequestedBy !== currentUserId) {
                items.push({
                    id: `termination-owner-${rentalData.rentalId}`,
                    title: "Termination action required",
                    message: "Owner requested lease termination. Please review and approve if you agree.",
                    time: "Lease update",
                });
            } else {
                items.push({
                    id: `termination-renter-${rentalData.rentalId}`,
                    title: "Termination request pending",
                    message: "Your lease termination request is waiting for owner approval.",
                    time: "Lease update",
                });
            }
        }

        myApplications.forEach((app) => {
            if (app.status === "approved") {
                items.push({
                    id: `app-${app.id}-approved`,
                    title: "Application approved",
                    message: `${app.propertyName} was approved by the owner.`,
                    time: `Submitted ${new Date(app.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`,
                });
            } else if (app.status === "rejected") {
                items.push({
                    id: `app-${app.id}-rejected-${app.rejectionReason || "none"}`,
                    title: "Application rejected",
                    message: app.rejectionReason
                        ? `${app.propertyName}: ${app.rejectionReason}`
                        : `${app.propertyName} was declined by the owner.`,
                    time: `Submitted ${new Date(app.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`,
                });
            } else {
                items.push({
                    id: `app-${app.id}-pending`,
                    title: "Application under review",
                    message: `${app.propertyName} is still being reviewed by the owner.`,
                    time: `Submitted ${new Date(app.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`,
                });
            }
        });

        return items.slice(0, 8);
    })();

    const unreadCount = notifications.filter((item) => !readNotificationIds.includes(item.id)).length;

    const toggleNotifications = () => {
        setShowNotifications((prev) => !prev);
        if (!showNotifications) {
            setReadNotificationIds((prev) => {
                const merged = new Set(prev);
                notifications.forEach((item) => merged.add(item.id));
                return Array.from(merged);
            });
        }
    };

    if (loading) {
        return (
            <div className="container p-2">
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (!hasActiveRental) {
        return (
            <div className="container p-2">
                <div className="heading mb-4">
                    <p className="fs-1 fw-semibold mb-0">{renterName}</p>
                    <p className="text-secondary fs-6 fw-normal mx-1">
                            {t('renterDashboard.welcomeSubtitle')}
                    </p>
                </div>

                <KycPromptModal kycStatus={kycStatus} role="renter" />
                <EmailVerificationBanner />

                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                    <div className="text-center" style={{ maxWidth: '600px' }}>
                        {/* Icon with background circle */}
                        <div className="position-relative d-inline-block mb-4">
                            <div 
                                className="rounded-circle d-flex align-items-center justify-content-center bg-light" 
                                style={{ width: '160px', height: '160px' }}
                            >
                                <FontAwesomeIcon 
                                    icon={faBuilding} 
                                    size="4x" 
                                    className="text-secondary"
                                    style={{ opacity: 0.3 }}
                                />
                            </div>
                            {/* Search icon badge */}
                            <div 
                                className="position-absolute bg-primary rounded-circle d-flex align-items-center justify-content-center" 
                                style={{ 
                                    width: '50px', 
                                    height: '50px', 
                                    bottom: '5px', 
                                    right: '5px',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                                }}
                            >
                                <FontAwesomeIcon 
                                    icon={faSearch} 
                                    className="text-white"
                                    size="lg"
                                />
                            </div>
                        </div>

                        {/* Heading */}
                        <h2 className="fw-bold mb-3" style={{ fontSize: '2rem' }}>
                               {t('renterDashboard.noActiveRental')}
                        </h2>

                        {/* Description */}
                        <p className="text-muted mb-4" style={{ fontSize: '1.1rem', lineHeight: '1.6' }}>
                               {t('renterDashboard.noActiveRentalDesc')}
                        </p>

                        {/* Browse Button */}
                        <button 
                            className="btn btn-primary btn-lg px-4 py-3 d-inline-flex align-items-center gap-2"
                            onClick={handleBrowseProperties}
                            style={{ 
                                borderRadius: '50px',
                                fontSize: '1rem',
                                fontWeight: '500',
                                transition: 'all 0.3s ease'
                            }}
                        >
                            <FontAwesomeIcon icon={faSearch} />
                            {t('renterDashboard.browseBtn')}
                            <FontAwesomeIcon icon={faArrowRight} />
                        </button>
                    </div>

                </div>

                                {renderApplicationsSection()}
            </div>
        );
    }

    return(
        <div className="container p-2">
            <KycPromptModal kycStatus={kycStatus} role="renter" />
            <EmailVerificationBanner />
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div className="heading">
                    <p className="fs-1 fw-semibold mb-0">{t('renterDashboard.dashboardTitle')}</p>
                    <p className="text-secondary fs-6 fw-normal mx-1">
                        {t('renterDashboard.welcomeBackName')} {renterName}
                    </p>
                </div>
                <div className="position-relative" ref={notificationRef}>
                    <button
                        type="button"
                        className="btn btn-light border position-relative"
                        onClick={toggleNotifications}
                        title="Notifications"
                    >
                        <FontAwesomeIcon icon={faBell} size="lg" className="text-secondary" />
                        {unreadCount > 0 && (
                            <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger" style={{ fontSize: '0.6rem' }}>
                                {unreadCount > 9 ? "9+" : unreadCount}
                            </span>
                        )}
                    </button>

                    {showNotifications && (
                        <div
                            className="card border-0 shadow"
                            style={{
                                position: "absolute",
                                top: "calc(100% + 10px)",
                                right: 0,
                                width: "360px",
                                maxWidth: "90vw",
                                zIndex: 20,
                            }}
                        >
                            <div className="card-body p-0">
                                <div className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom">
                                    <h6 className="mb-0 fw-bold">{t('renterDashboard.notifications')}</h6>
                                    <small className="text-muted">{notifications.length} item(s)</small>
                                </div>
                                <div style={{ maxHeight: "320px", overflowY: "auto" }}>
                                    {notifications.length === 0 ? (
                                        <div className="p-3 text-muted small">{t('renterDashboard.noNotifications')}</div>
                                    ) : (
                                        notifications.map((item) => (
                                            <div key={item.id} className="px-3 py-2 border-bottom">
                                                <div className="fw-semibold small">{item.title}</div>
                                                <div className="text-muted small">{item.message}</div>
                                                <div className="text-muted" style={{ fontSize: "0.75rem" }}>{item.time}</div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Property Tabs - Only show if multiple rentals */}
            {allRentals.length > 1 && (
                <div className="mb-4">
                    <ul className="nav nav-pills gap-2">
                        {allRentals.map((rental, index) => (
                            <li className="nav-item" key={index}>
                                <button
                                    className={`nav-link ${activeTabIndex === index ? 'active' : ''}`}
                                    onClick={() => {
                                        setActiveTabIndex(index);
                                        setRentalData(allRentals[index]);
                                    }}
                                    style={{
                                        borderRadius: '8px',
                                        fontWeight: '500'
                                    }}
                                >
                                    <FontAwesomeIcon icon={faBuilding} className="me-2" />
                                    {rental.propertyName}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Active Lease Card */}
            <div className="card mb-4 border-0 shadow-sm" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                <div className="row g-0">
                    <div className="col-md-5 position-relative" style={{ maxHeight: '300px', overflow: 'hidden' }}>
                        <span className="badge bg-success position-absolute top-0 start-0 m-3" style={{ zIndex: 1 }}>
                            <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                               {t('renterDashboard.activeLease')}
                        </span>
                        <img 
                            src={rentalData.image} 
                            alt={rentalData.propertyName}
                            style={{ 
                                width: '100%', 
                                height: '300px', 
                                objectFit: 'cover'
                            }}
                        />
                    </div>
                    <div className="col-md-7">
                        <div className="card-body p-4">
                            <h3 className="fw-bold mb-2">{rentalData.propertyName}</h3>
                            <p className="text-muted mb-3">
                                <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2" />
                                {rentalData.address} · {rentalData.city}
                            </p>
                            
                            <div className="d-flex gap-4 mb-4">
                                <div className="d-flex align-items-center gap-2">
                                    <FontAwesomeIcon icon={faBed} className="text-secondary" />
                                     <span>{rentalData.bedrooms} {t('renterDashboard.beds')}</span>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <FontAwesomeIcon icon={faBath} className="text-secondary" />
                                     <span>{rentalData.bathrooms} {t('renterDashboard.bath')}</span>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <FontAwesomeIcon icon={faRulerCombined} className="text-secondary" />
                                     <span>{rentalData.sqft} {t('renterDashboard.sqft')}</span>
                                </div>
                            </div>

                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="text-muted mb-1" style={{ fontSize: '0.9rem' }}>{t('renterDashboard.monthlyRent')}</p>
                                    <h2 className="fw-bold mb-0">${rentalData.monthlyRent.toLocaleString()}</h2>
                                </div>
                                <button className="btn btn-primary px-4 py-2">
                                    <FontAwesomeIcon icon={faCreditCard} className="me-2" />
                                    {t('renterDashboard.payRent')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Info Cards Row */}
            <div className="row g-3 mb-4">
                <div className="col-md-4">
                    <div className="card h-100 border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                        <div className="card-body p-4">
                            <div className="d-flex align-items-center mb-3">
                                <div className="bg-light rounded-circle p-3 me-3">
                                    <FontAwesomeIcon icon={faCalendar} className="text-secondary" size="lg" />
                                </div>
                                <div>
                                    <p className="text-muted mb-0 small">{t('renterDashboard.nextPaymentDue')}</p>
                                </div>
                            </div>
                            <h4 className="fw-bold mb-1">{rentalData.nextPaymentDue}</h4>
                            <p className="text-warning mb-0 small">
                                <FontAwesomeIcon icon={faBell} className="me-1" />
                                  {t('renterDashboard.dueIn')} {rentalData.daysUntilDue} {t('renterDashboard.days')}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="col-md-4">
                    <div className="card h-100 border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                        <div className="card-body p-4">
                            <div className="d-flex align-items-center mb-3">
                                <div className="bg-light rounded-circle p-3 me-3">
                                    <FontAwesomeIcon icon={faFileContract} className="text-secondary" size="lg" />
                                </div>
                                <div>
                                    <p className="text-muted mb-0 small">{t('renterDashboard.leaseEnds')}</p>
                                </div>
                            </div>
                            <h4 className="fw-bold mb-1">{rentalData.leaseEnds}</h4>
                            <p className="text-primary mb-0 small">
                                <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                                  {rentalData.monthsRemaining} {t('renterDashboard.monthsRemaining')}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="col-md-4">
                    <div className="card h-100 border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                        <div className="card-body p-4">
                            <div className="d-flex align-items-center mb-3">
                                <div className="bg-light rounded-circle p-3 me-3">
                                    <FontAwesomeIcon icon={faCommentDots} className="text-secondary" size="lg" />
                                </div>
                                <div>
                                    <p className="text-muted mb-0 small">{t('renterDashboard.landlord')}</p>
                                </div>
                            </div>
                            <h5 className="fw-bold mb-1">{rentalData.landlord}</h5>
                            <p className="text-success mb-0 small">
                                <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                                {rentalData.landlordStatus}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Quick Actions */}
            {/* Termination Alert Banner - Only show when OWNER requested termination */}
            {rentalData.terminationRequested && rentalData.terminationRequestedBy !== currentUserId && (
                <div className="alert alert-danger mb-4" role="alert">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h6 className="alert-heading mb-1">
                                  ⚠️ {t('renterDashboard.ownerRequestedTermination')}
                            </h6>
                            <p className="mb-0 small">
                                  <strong>{t('renterDashboard.reason')}</strong> {rentalData.terminationReason}
                            </p>
                        </div>
                        <div>
                            <button 
                                className="btn btn-sm btn-danger"
                                onClick={handleApproveTermination}
                            >
                                  {t('renterDashboard.approveTermination')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="row g-3 mb-4">
                <div className="col-6 col-md-3">
                    <div className="card border-0 shadow-sm text-center" style={{ borderRadius: '12px', cursor: 'pointer' }}>
                        <div className="card-body p-4">
                            <div className="bg-light rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                                <FontAwesomeIcon icon={faCreditCard} className="text-secondary" size="xl" />
                            </div>
                            <p className="mb-0 fw-semibold">{t('renterDashboard.payRent')}</p>
                        </div>
                    </div>
                </div>
                <div className="col-6 col-md-3">
                    <div className="card border-0 shadow-sm text-center" style={{ borderRadius: '12px', cursor: 'pointer' }}>
                        <div className="card-body p-4">
                            <div className="bg-light rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                                <FontAwesomeIcon icon={faWrench} className="text-secondary" size="xl" />
                            </div>
                            <p className="mb-0 fw-semibold">{t('renterDashboard.maintenance')}</p>
                        </div>
                    </div>
                </div>
                <div className="col-6 col-md-3">
                    <div className="card border-0 shadow-sm text-center" style={{ borderRadius: '12px', cursor: 'pointer' }}>
                        <div className="card-body p-4">
                            <div className="bg-light rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                                <FontAwesomeIcon icon={faFileContract} className="text-secondary" size="xl" />
                            </div>
                            <p className="mb-0 fw-semibold">{t('renterDashboard.lease')}</p>
                        </div>
                    </div>
                </div>
                <div className="col-6 col-md-3">
                    {!rentalData.terminationRequested ? (
                        <div 
                            className="card border-0 shadow-sm text-center bg-danger bg-opacity-10" 
                            style={{ borderRadius: '12px', cursor: 'pointer' }}
                            onClick={handleRequestTermination}
                        >
                            <div className="card-body p-4">
                                <div className="bg-danger bg-opacity-25 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                                    <FontAwesomeIcon icon={faFileContract} className="text-danger" size="xl" />
                                </div>
                                <p className="mb-0 fw-semibold text-danger">{t('renterDashboard.endLease')}</p>
                            </div>
                        </div>
                    ) : rentalData.terminationRequestedBy === currentUserId ? (
                        <div 
                            className="card border-0 shadow-sm text-center bg-warning bg-opacity-10" 
                            style={{ borderRadius: '12px', cursor: 'pointer' }}
                            onClick={handleCancelTermination}
                        >
                            <div className="card-body p-4">
                                <div className="bg-warning bg-opacity-25 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                                    <FontAwesomeIcon icon={faFileContract} className="text-warning" size="xl" />
                                </div>
                                <p className="mb-0 fw-semibold text-warning">{t('renterDashboard.cancelRequest')}</p>
                            </div>
                        </div>
                    ) : (
                        <div className="card border-0 shadow-sm text-center bg-info bg-opacity-10" style={{ borderRadius: '12px' }}>
                            <div className="card-body p-4">
                                <div className="bg-info bg-opacity-25 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                                    <FontAwesomeIcon icon={faFileContract} className="text-info" size="xl" />
                                </div>
                                <p className="mb-0 fw-semibold text-info">{t('renterDashboard.ownerRequested')}</p>
                            </div>
                        </div>
                    )}
                </div>
                <div className="col-6 col-md-3">
                    <div 
                        className="card border-0 shadow-sm text-center" 
                        style={{ borderRadius: '12px', cursor: 'pointer' }}
                        onClick={() => navigate("/messages")}
                    >
                        <div className="card-body p-4">
                            <div className="bg-light rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                                <FontAwesomeIcon icon={faCommentDots} className="text-secondary" size="xl" />
                            </div>
                            <p className="mb-0 fw-semibold">{t('renterDashboard.contact')}</p>
                        </div>
                    </div>
                </div>
                {canReview && (
                    <div className="col-6 col-md-3">
                        <div 
                            className="card border-0 shadow-sm text-center bg-warning bg-opacity-10" 
                            style={{ borderRadius: '12px', cursor: 'pointer' }}
                            onClick={handleWriteReview}
                        >
                            <div className="card-body p-4">
                                <div className="bg-warning bg-opacity-25 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                                    <FontAwesomeIcon icon={faStar} className="text-warning" size="xl" />
                                </div>
                                <p className="mb-0 fw-semibold text-warning">{t('renterDashboard.writeReview')}</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Termination Modal */}
            {showTerminationModal && (
                <div 
                    className="modal d-block" 
                    style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                    onClick={() => setShowTerminationModal(false)}
                >
                    <div 
                        className="modal-dialog modal-dialog-centered"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="modal-content">
                            <div className="modal-header bg-danger text-white">
                                <h5 className="modal-title">{t('renterDashboard.requestTerminationTitle')}</h5>
                                <button 
                                    type="button" 
                                    className="btn-close btn-close-white" 
                                    onClick={() => setShowTerminationModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="alert alert-warning">
                                    <small>
                                        <strong>Note:</strong> {t('renterDashboard.terminationNote')}
                                    </small>
                                </div>

                                <div className="mb-3">
                                    <label className="form-label fw-semibold">{t('renterDashboard.terminationReasonLabel')} *</label>
                                    <textarea 
                                        className="form-control"
                                        rows="4"
                                            placeholder={t('renterDashboard.terminationPlaceholder')}
                                        value={terminationReason}
                                        onChange={(e) => setTerminationReason(e.target.value)}
                                    ></textarea>
                                    <small className="text-muted">
                                        {t('renterDashboard.terminationHint')}
                                    </small>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary" 
                                    onClick={() => setShowTerminationModal(false)}
                                    disabled={submittingTermination}
                                >
                                        {t('renterDashboard.cancel')}
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-danger"
                                    onClick={handleSubmitTermination}
                                    disabled={submittingTermination || !terminationReason.trim()}
                                >
                                    {submittingTermination ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                {t('renterDashboard.submitting')}
                                        </>
                                    ) : (
                                            t('renterDashboard.submitRequest')
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Review Modal */}
            {showReviewModal && (
                <div 
                    className="modal d-block" 
                    style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                    onClick={() => setShowReviewModal(false)}
                >
                    <div 
                        className="modal-dialog modal-dialog-centered"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="modal-content">
                            <div className="modal-header bg-warning text-dark">
                                <h5 className="modal-title">{t('renterDashboard.writeReviewTitle')}</h5>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={() => setShowReviewModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-4">
                                    <label className="form-label fw-semibold">{t('renterDashboard.propertyLabel')}</label>
                                    <p className="text-muted">{rentalData.propertyName}</p>
                                </div>
                                
                                <div className="mb-4">
                                    <label className="form-label fw-semibold d-block">Rating</label>
                                    <div className="d-flex gap-2">
                                        {renderStars(reviewRating, true)}
                                    </div>
                                    <small className="text-muted">Click on a star to rate (1-5 stars)</small>
                                </div>

                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Your Review</label>
                                    <textarea
                                        className="form-control"
                                        rows="5"
                                        placeholder="Share your experience with this property..."
                                        value={reviewText}
                                        onChange={(e) => setReviewText(e.target.value)}
                                    ></textarea>
                                    <small className="text-muted">Tell others about your rental experience</small>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary" 
                                    onClick={() => setShowReviewModal(false)}
                                    disabled={submittingReview}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-warning"
                                    onClick={handleSubmitReview}
                                    disabled={submittingReview || !reviewText.trim()}
                                >
                                    {submittingReview ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Submitting...
                                        </>
                                    ) : (
                                        "Submit Review"
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Recent Activity */}
            <div className="card border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                <div className="card-body p-4">
                    <h5 className="fw-bold mb-4">Recent Activity</h5>
                    
                    {rentalData.recentActivity.map((activity, index) => (
                        <div key={index} className="d-flex align-items-start mb-3 pb-3" style={{ borderBottom: index < rentalData.recentActivity.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                            <div className={`bg-${activity.color}-subtle rounded-circle d-flex align-items-center justify-content-center me-3`} style={{ width: '45px', height: '45px', minWidth: '45px' }}>
                                <FontAwesomeIcon icon={activity.icon} className={`text-${activity.color}`} />
                            </div>
                            <div className="flex-grow-1">
                                <div className="d-flex justify-content-between align-items-start">
                                    <div>
                                        <h6 className="mb-1 fw-semibold">{activity.title}</h6>
                                        <p className="text-muted mb-0 small">{activity.description}</p>
                                    </div>
                                    <span className="text-muted small">{activity.date}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {renderApplicationsSection()}
        </div>
    );
};
export default RenterDashboard;